// WHAT: Aspect card component for displaying and editing aspect configurations
// WHY:  Encapsulates aspect display and editing UI for better component reusability
// DOMAIN: Settings → TimeTracQ → Workspace Management
// DEPENDENCIES: UI components (Card, Input, Select, Label, Stack, Badge, Heading)
// RISKS:
//   - NOTE: Changes are immediate but not persisted until save
//   - WARN: No validation of value type or range

import type { AspectConfig } from "../types";

import { Card, Input, Select, Label, Stack, Badge, Heading } from "@/components/ui";

/**
 * WHAT:
 *   Helper function to get Badge variant based on aspect status.
 *
 * @param status - Aspect status (Include, Exclude, or other)
 *
 * @returns {string} Badge variant name
 *
 */
function getStatusBadgeVariant(status: string): "default" | "destructive" | "success" {
  if (status === "Include") {
    return "success";
  }
  if (status === "Exclude") {
    return "destructive";
  }
  return "default";
}

/**
 * WHAT:
 *   Component for displaying and editing a single aspect configuration.
 *
 * WHY:
 *   Provides reusable UI for aspect editing with all controls in one place.
 *
 * PROPS:
 *   - aspectName: Name of the aspect
 *   - aspect: Aspect configuration object
 *   - onUpdateHours: Callback when hours value changes
 *   - onUpdateStatus: Callback when status changes
 *   - onUpdateColor: Callback when color changes
 *   - onToggleBoolean: Callback when boolean flags are toggled
 *
 * BEHAVIOR:
 *   - Displays aspect name, color, and status badge
 *   - Provides inputs for hours, color picker, status dropdown
 *   - Shows three boolean toggles (carryover, count_hours, count_plan)
 */
interface AspectCardProperties {
  aspect: AspectConfig;
  aspectName: string;
  onToggleBoolean: (field: "carryover" | "count_hours" | "count_plan") => void;
  onUpdateColor: (color: string) => void;
  onUpdateHours: (hours: string) => void;
  onUpdateStatus: (status: string) => void;
}

/**
 * WHAT:
 *   React component that renders a card for a single TimeTracQ aspect.
 *
 * WHY:
 *   Encapsulates the UI and interaction logic for an individual aspect.
 *
 * @param root0 - Component props
 *
 * @param root0.aspect - Aspect configuration object
 *
 * @param root0.aspectName - Name of the aspect
 *
 * @param root0.onToggleBoolean - Callback to toggle boolean properties
 *
 * @param root0.onUpdateColor - Callback to update aspect color
 *
 * @param root0.onUpdateHours - Callback to update aspect hours
 *
 * @param root0.onUpdateStatus - Callback to update aspect status
 *
 * @returns {JSX.Element} Aspect card component
 *
 */
export function AspectCard({
  aspect,
  aspectName,
  onToggleBoolean,
  onUpdateColor,
  onUpdateHours,
  onUpdateStatus,
}: Readonly<AspectCardProperties>) {
  return (
    <Card className="bg-slate-700/50 border-slate-600/30" padding="md" variant="default">
      <div className="flex items-center justify-between mb-4">
        <Heading className="text-md font-medium text-white" level={5}>
          {aspectName}
        </Heading>
        <div className="flex items-center space-x-2">
          <div
            className="w-4 h-4 rounded-full border-2 border-slate-500"
            style={{ backgroundColor: aspect.color }}
          />
          <Badge size="sm" style="subtle" variant={getStatusBadgeVariant(aspect.status)}>
            {aspect.status}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Stack direction="col" gap={2}>
          <Label className="text-slate-400 text-xs uppercase tracking-wide">Hours</Label>
          <Input
            min="0"
            onChange={(e) => {
              onUpdateHours(e.target.value);
            }}
            size="sm"
            step="0.5"
            type="number"
            value={aspect.hours}
          />
        </Stack>

        <Stack direction="col" gap={2}>
          <Label className="text-slate-400 text-xs uppercase tracking-wide">Color</Label>
          <div className="flex items-center space-x-2">
            <input
              className="w-12 h-10 bg-slate-600/50 border border-slate-500/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 cursor-pointer"
              onChange={(e) => {
                onUpdateColor(e.target.value);
              }}
              type="color"
              value={aspect.color}
            />
            <span className="text-xs text-slate-400">{aspect.color}</span>
          </div>
        </Stack>

        <div className="sm:col-span-2">
          <Stack direction="col" gap={2}>
            <Label className="text-slate-400 text-xs uppercase tracking-wide">Status</Label>
            <Select
              onChange={(value) => {
                const statusValue = Array.isArray(value) ? value[0] : value;
                if (typeof statusValue === "string" && statusValue) {
                  onUpdateStatus(statusValue);
                }
              }}
              options={[
                { label: "Include - Active planning", value: "Include" },
                { label: "Exclude - Excluded", value: "Exclude" },
                { label: "Inactive - Disabled", value: "Inactive" },
              ]}
              size="sm"
              value={aspect.status}
            />
          </Stack>
        </div>

        <div className="sm:col-span-2">
          <div className="grid grid-cols-3 gap-3">
            <label className="flex items-center space-x-2 p-2 rounded-lg hover:bg-slate-600/30 transition-colors cursor-pointer">
              <input
                checked={aspect.carryover}
                className="rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500"
                onChange={() => {
                  onToggleBoolean("carryover");
                }}
                type="checkbox"
              />
              <span className="text-xs text-slate-300">Carryover</span>
            </label>

            <label className="flex items-center space-x-2 p-2 rounded-lg hover:bg-slate-600/30 transition-colors cursor-pointer">
              <input
                checked={aspect.count_hours}
                className="rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500"
                onChange={() => {
                  onToggleBoolean("count_hours");
                }}
                type="checkbox"
              />
              <span className="text-xs text-slate-300">Count Hours</span>
            </label>

            <label className="flex items-center space-x-2 p-2 rounded-lg hover:bg-slate-600/30 transition-colors cursor-pointer">
              <input
                checked={aspect.count_plan}
                className="rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500"
                onChange={() => {
                  onToggleBoolean("count_plan");
                }}
                type="checkbox"
              />
              <span className="text-xs text-slate-300">Count Plan</span>
            </label>
          </div>
        </div>
      </div>
    </Card>
  );
}
